/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// B-5: the website's language-prefixing rule is already implemented once, in
// desksharePath() (English takes no prefix; every other language takes a
// two-letter one). Reused here rather than written a second time.
import { desksharePath, detectLanguage } from "@/lib/auth-redirect";
import { toSupportedLang } from "@/lib/supported-languages";
import {
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
  TSL_45K_PRODUCT_ID,
  TSL_EXTRA_WORDS_PRODUCT_ID,
  TSL_TRIAL_PRODUCT_ID,
  MSR_V6_PRODUCT_ID,
  DICTATION_PRO_V2_PRODUCT_ID,
  MSR_PRO_PRODUCT_ID,
  AUTO_FTP_V8_PRODUCT_ID,
  SECURITY_MONITOR_PRO_PRODUCT_ID,
  WEBCAM_MONITOR_PRODUCT_ID,
  QLM_PRODUCT_ID_TSL_TRIAL,
  QLM_PRODUCT_ID_TSL_45K,
  QLM_PRODUCT_ID_TSL_100K,
  QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL,
  // DD-3 (FCR Pass 6): unified product QLM IDs (were 0/missing — now confirmed)
  QLM_PRODUCT_ID_MSR_V6,
  QLM_PRODUCT_ID_DICTATION_PRO_V2,
  QLM_PRODUCT_ID_MSR_PRO,
  QLM_PRODUCT_ID_AUTO_FTP_V8,
  QLM_PRODUCT_ID_SECURITY_MONITOR_PRO,
  QLM_PRODUCT_ID_WEBCAM_MONITOR,
} from "@/config/product-ids";
import { envOr as _envOr } from "@/config/product-ids";

// Re-export all product IDs and helpers so existing importers of this file continue to work
// Sakshi 6: isDocumentTranslatorProduct was in cart-limits but imported from constants in use-checkout.ts
export {
  isDocumentTranslatorProduct,
  isStandaloneCartProduct,
} from "@/lib/cart-limits";

export {
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
  TSL_45K_PRODUCT_ID,
  TSL_EXTRA_WORDS_PRODUCT_ID,
  TSL_TRIAL_PRODUCT_ID,
  MSR_V6_PRODUCT_ID,
  DICTATION_PRO_V2_PRODUCT_ID,
  MSR_PRO_PRODUCT_ID,
  AUTO_FTP_V8_PRODUCT_ID,
  SECURITY_MONITOR_PRO_PRODUCT_ID,
  WEBCAM_MONITOR_PRODUCT_ID,
  // QLM product ID constants (F-1, S343)
  QLM_PRODUCT_ID_TSL_TRIAL,
  QLM_PRODUCT_ID_TSL_45K,
  QLM_PRODUCT_ID_TSL_100K,
  QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL,
};

export const SUPPORT_EMAIL = "support@deskshare.com";
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

// U3: Chat-first support URL — matches the centralised backend value in
// apps/notifications/templates.py. Update both locations together if the URL changes.
//
// S660 kept this value byte-equal with the server's own `SUPPORT_CHAT_URL`
// setting so a customer meets the same support address in an e-mail and on a
// screen, and argued that deriving it from `DESKSHARE_WEBSITE_URL` would make
// the browser say `www.deskshare.com` while every e-mail kept saying
// `deskshare.com`.
//
// SA-13 (S712) SPLITS THAT QUESTION IN TWO, because it is two questions.
//
// This constant is still what the SERVER sends, and it still must not move on
// its own: that half of S660's reasoning stands and nothing below changes it.
// What S660 did not weigh is that the browser used this value's HOST as well as
// its path, so every support link in the feature and staging shops opened the
// LIVE website. That is not a cosmetic divergence between an e-mail and a
// screen; it is a tester or a customer in a test environment being sent to
// production, and it is why Soham A. asked for `feature1.deskshare.com` to be
// written into the code.
//
// So `getSupportUrl()` below takes the PATH from here and the HOST from
// `DESKSHARE_WEBSITE_URL`. In production those differ only by the `www.`
// prefix, which the website serves either way; in every other environment the
// difference is the whole point. Changing this string still changes both ends,
// which is what "move on BOTH sides" was protecting.
export const SUPPORT_CHAT_URL = "https://deskshare.com/product-support/";

/**
 * The support page in the customer's language. B-12 (S703).
 *
 * The "Need help" link and its seven siblings were translated on screen and then
 * opened the ENGLISH support page, for every customer in every language. The
 * Deskshare Website serves that page under each language prefix -- its
 * `isProductSupportPath()` accepts `/product-support` after any of them -- so
 * `/fr/product-support/` is a real page and nothing on that side has to change.
 *
 * `desksharePath()` is reused rather than a prefix being written here, because
 * the website's rule is not the shop's: English takes NO prefix there. Writing
 * `/en/product-support/` by hand would produce a Page Not Found for the majority
 * language, which is the exact defect that helper exists to prevent.
 *
 * The language is put through `toSupportedLang()` first, and that is not
 * belt-and-braces. `detectLanguage()` reads the FIRST PATH SEGMENT of whatever
 * address the browser is on, and answers "en" only when that segment looks like
 * a two-letter code. A shop address always carries one — but `pages/error.tsx`
 * is one of this function's eight callers, and it renders precisely when the
 * address is not one the shop recognises. Without the whitelist, a visitor on
 * `/cart` would be offered `https://deskshare.com/cart/product-support/`, and
 * one on `/zz/...` a language prefix the website does not serve. Both are
 * broken links on the page whose entire purpose is to get somebody help.
 *
 * The constant above is unchanged and is still what the SERVER sends; this only
 * decides which language the browser opens it in.
 */
export function getSupportUrl(lang?: string): string {
  // SA-13 (S712): the HOST follows the environment; only the path comes from
  // the constant above.
  //
  // `SUPPORT_CHAT_URL` is deliberately fixed to production because the server
  // sends that exact string in e-mails and the two ends are byte-equal -- that
  // reasoning is above and it still holds. What it did NOT reason about is the
  // browser: taking the host from it too meant every support link in the
  // feature and staging shops opened the LIVE website, which is why Soham A.
  // asked for `feature1.deskshare.com` to be written into the code. No
  // environment's address belongs in the code; `DESKSHARE_WEBSITE_URL` is the
  // value that already knows which website this build belongs to.
  //
  // The path is still read from the constant rather than written again here, so
  // if the support page ever moves there is one place to change.
  const url = new URL(DESKSHARE_WEBSITE_URL);
  url.pathname = desksharePath(
    new URL(SUPPORT_CHAT_URL).pathname,
    toSupportedLang(lang ?? detectLanguage()),
  );
  return url.toString();
}

/**
 * S660 -- the Deskshare marketing website, for THIS environment.
 *
 * This was a hard-coded production address while `src/config/configuration.ts`
 * read the same address from `VITE_DESKSHARE_WEBSITE_URL`, and the constant is
 * what the screens actually rendered: the cart's website link, the cart error
 * boundary, the trial status page and Browse Products. So a feature or staging
 * shop sent every one of those customers to the LIVE marketing site. It is the
 * browser half of the defect closed on the server in S659, where the e-mail
 * templates had the address the same way.
 *
 * It now reads the same variable the configuration module reads, with the
 * production address as the fallback -- a quoted default alone on its line,
 * which is the one place a default belongs. `configuration.ts` refuses to load a
 * built bundle that has not set the variable, so the fallback is reachable only
 * in development and under the test runner.
 *
 * EVERY OTHER ADDRESS ON THIS WEBSITE IS DERIVED FROM IT, below and in
 * getProductPageUrl(): the privacy policy, the refund-policy fallback and the
 * three installer files. Each of those wrote the production host out again, so
 * moving this one alone would have left four screens following the environment
 * and five links still pointing at production -- one fault turned into five.
 */
export const DESKSHARE_WEBSITE_URL = _envOr(
  "VITE_DESKSHARE_WEBSITE_URL",
  "https://www.deskshare.com",
).replace(/\/+$/, "");

/**
 * The website's privacy policy (Session 743). It was `/privacy_policy.htm`, the
 * old site's page; the website's own routes (its latest package, `main.tsx`)
 * publish `/privacy-policy`, with the language before it for every language but
 * English, and answer any other path with their not-found page. So the trial
 * sign-up page's privacy link led nowhere. `privacyPolicyUrl(lang)` gives the
 * page in the reader's language; the constant is the English page.
 */
export const PRIVACY_POLICY_URL = `${DESKSHARE_WEBSITE_URL}/privacy-policy`;

export function privacyPolicyUrl(lang?: string): string {
  return `${DESKSHARE_WEBSITE_URL}${desksharePath("/privacy-policy", lang)}`;
}

/**
 * Issue 5 (FCR Pass 9 Correction): Centralised refund policy address.
 * Approved address — www prefix matches every other Deskshare constant;
 * no trailing slash matches the convention every published page follows.
 * Keep in step with the website route, sitemap entry, footer link, and
 * the Stripe Checkout policy setting (all must carry the identical string).
 * The website team must publish the page before any of those links are live.
 */
// SB-2 (Pass 11): one address for the refund policy, matching the backend's
// REFUND_POLICY_URL setting. Read from configuration so a feature environment can
// point somewhere else without a release, with the production address as the
// fallback for the moment before configuration loads.
//
// Decision G (S652): the website has never published this page -- every address
// under it answers with a redirect to the marketing home page -- so the shop
// serves the policy itself at /{lang}/refund-policy (src/pages/refund-policy.tsx)
// and this variable is now SET, per environment, to that route:
//   feature:    https://feature.shop.deskshare.com/en/refund-policy
//   production: https://shop.deskshare.com/en/refund-policy
// Where the website publishes its own page these become the ?standalone=true form
// (https://feature1.deskshare.com/refund-policy?standalone=true on feature and
// staging now; https://www.deskshare.com/refund-policy?standalone=true in
// production on the day the new website is live there). The shop's route then
// forwards to it (refundPolicyForwardTarget below), so e-mails already sent and
// Stripe pages carrying the shop's address reach the one policy; it shows its own
// stand-in text only while this variable still names the shop's route. Nothing
// else changes: the places that link to the policy all read this constant, which
// is the point of it being configuration rather than a literal.
//
// The fallback below is deliberately left as the website's own refund-policy
// address. It is not a working page today, which is why validate_env now
// REQUIRES the variable in production as well as staging -- a deployment that
// omits it fails at start-up instead of quietly serving a link that redirects to
// a home page.
//
// S660: the host is DESKSHARE_WEBSITE_URL rather than the production literal it
// used to repeat, so an environment that sets only the website address still
// falls back onto its OWN site rather than the live one.
export const REFUND_POLICY_URL = _envOr(
  "VITE_REFUND_POLICY_URL",
  `${DESKSHARE_WEBSITE_URL}/refund-policy`,
);

/** A leading two-letter language segment, e.g. "/de" in "/de/refund-policy". */
const LANGUAGE_SEGMENT = /^\/[a-z]{2}(?=\/)/i;

/**
 * True when `configured` names the SHOP's own stand-in route -- a language-first
 * path like /en/refund-policy on a host that is not the website's -- rather than
 * a page on the website. The website publishes English without a language
 * segment, so its address never looks like the shop's.
 */
export function isShopRefundPolicyRoute(configured: string = REFUND_POLICY_URL): boolean {
  try {
    const url = new URL(configured);
    const website = new URL(DESKSHARE_WEBSITE_URL);
    return url.origin !== website.origin && LANGUAGE_SEGMENT.test(url.pathname);
  } catch {
    return false;
  }
}

/**
 * The refund policy in the reader's language.
 *
 * When REFUND_POLICY_URL is on the website, its path is rebuilt with
 * desksharePath() -- the one copy of the website's language rule (English
 * takes no prefix) -- and its query (?standalone=true) is kept. When it is the
 * shop's own route, or cannot be read, it is returned unchanged: the shop's page
 * follows the language switcher itself.
 */
export function refundPolicyUrl(lang?: string, configured: string = REFUND_POLICY_URL): string {
  if (isShopRefundPolicyRoute(configured)) return configured;
  try {
    const url = new URL(configured);
    const path = url.pathname.replace(LANGUAGE_SEGMENT, "").replace(/\/+$/, "") || "/";
    return `${url.origin}${desksharePath(path, lang)}${url.search}${url.hash}`;
  } catch {
    return configured;
  }
}

/**
 * Where the shop's /{lang}/refund-policy route must send the browser, or null
 * when it should show its own stand-in page (REFUND_POLICY_URL still names the
 * shop's route, which is production until the new website is live there).
 */
export function refundPolicyForwardTarget(
  lang?: string,
  configured: string = REFUND_POLICY_URL,
): string | null {
  return isShopRefundPolicyRoute(configured) ? null : refundPolicyUrl(lang, configured);
}

/**
 * Decision G / N-12 (S652) -- the refund rule the shop actually enforces.
 *
 * Read on the server from backend/config/settings.py (REFUND_WINDOW_DAYS,
 * overridable per environment) and repeated here so the published policy page
 * can state it in words a customer can read. There is no per-product limit and
 * no limit on how many orders an account may refund: the approved Refund Policy
 * promises a refund within 14 days of each order.
 *
 * A plain number rather than a second environment variable on purpose: an
 * environment that changed the window without changing the page would publish a
 * policy it does not apply (N-12). A back-end guard test asserts this value
 * matches the default in settings.py.
 */
export const REFUND_WINDOW_DAYS = 14;

/**
 * S270 / FCR-262 v5.0 / TE-2a — Canonical app-name to display-name mapping.
 *
 * Trial.app_name in the backend stores the canonical concatenated form that
 * the desktop applications send (for example "TextSpeakerLive"). Rendering
 * that string verbatim in the UI looks unprofessional. Use displayAppName()
 * at every render site instead of the raw value.
 *
 * Keep this map in sync with backend/apps/notifications/templates.py
 * _APP_NAME_DISPLAY — both sides read from a stable canonical-name contract.
 */
export const APP_NAME_DISPLAY: Record<string, string> = {
  TextSpeakerLive: "Text Speaker Live",
  DictationPro: "Dictation Pro",
  MyScreenRecorder: "My Screen Recorder",
  AutoFTPManager: "Auto FTP Manager",
  WebCamMonitor: "WebCam Monitor",
  SecurityMonitorPro: "Security Monitor Pro",
};

/**
 * Returns the human-readable display version of a canonical app name.
 * Already-spaced or unknown values pass through unchanged so the caller
 * never produces an empty string. Falls back to "your application" when
 * the input is null/undefined/empty.
 */
export function displayAppName(appName?: string | null): string {
  if (!appName) {
    return "your application";
  }
  return APP_NAME_DISPLAY[appName] ?? appName;
}

/**
 * S270 / FCR-262 v5.0 / TE-3 — Trial verification error codes.
 *
 * The backend now returns a stable error_code on every error response from
 * /trials/verify and /trials/verify-token, so the frontend no longer has to
 * substring-match the human-readable error message. Keep these codes in
 * sync with backend/apps/trials/views.py.
 */
export const TRIAL_VERIFY_ERROR_CODE = {
  INVALID_FORMAT: "INVALID_FORMAT",
  INVALID_TOKEN: "INVALID_TOKEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
} as const;

export type TrialVerifyErrorCode =
  (typeof TRIAL_VERIFY_ERROR_CODE)[keyof typeof TRIAL_VERIFY_ERROR_CODE];

export const enum SOCKET_EVENTS {
  REGISTER_WINDOWS = "register_windows",
  REGISTER_WEB = "register_web",
  PAYMENT_SUCCEEDED = "payment_succeeded",
  PAYMENT_SUCCEEDED_ERROR = "payment_succeeded_error",
  PAYMENT_FAILED = "payment_failed",
  LICENSE_ACTIVATED = "license_activated",
  LICENSE_DEACTIVATE = "license-deactivated",
  // S561: the three terminal replies of the desktop sign-in licence handoff.
  // LICENSE_ERROR is new -- it covers a lookup that could not be completed,
  // which previously produced no reply at all and left the page waiting.
  LICENSE_INFO = "license-info",
  LICENSE_NOT_FOUND = "license-not-found",
  LICENSE_ERROR = "license-error",
  PING = "ping",
  PONG = "pong",
  // I-10: Document Translator real-time events — previously missing from the enum
  UPDATE_DOCUMENT_STATUS = "update-document-status",
  TRANSLATION_ERROR = "translation-error",
  TRANSLATION_FAILED = "translation-failed",
  // S428/Change 11: DT word-credit balance update
  CREDITS_UPDATED = "credits-updated",
  // Finding 7 (S463 Pass 10): refund/dispute revoked a licence or cancelled a
  // subscription — tells an open Account Dashboard to re-fetch orders & licences.
  DASHBOARD_REFRESH = "dashboard_refresh",
}

/**
 * QLM product ID to Stripe product ID mapping (desktop-app QLM IDs → Stripe).
 * Used when the desktop sends a numeric QLM product ID and we need to resolve
 * the Stripe product for license lookup and routing.
 * Source: Backend QLM_TO_STRIPE_PRODUCT_MAPPING (apps/core/constants.py)
 *
 * S449: Keys are now derived from the canonical QLM_PRODUCT_ID_* constants
 * rather than hardcoded numeric literals. The old literals were stale and
 * wrong: Text Speaker Live was keyed under "30022"/"30023" (its real live QLM
 * ProductIDs are 25 = Basic/45K and 27 = Professional/100K, confirmed by
 * Sanjeev), and "27" was hardcoded to a My Screen Recorder product whose real
 * QLM ID is 1811, while 27 is TSL 100K. That collision meant a TSL 100K desktop
 * client sending ProductID 27 resolved to My Screen Recorder. Building the map
 * from the constants keeps it in lockstep with the backend and the
 * env-configured IDs, so this drift cannot recur.
 */
export const stripeProductMapping: Record<string, string> = {
  [QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL]: LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL]: LEGACY_MSR_ANNUAL_PRODUCT_ID,
  // Issue 10 (FCR Pass 9): one TSL entry only. Both aliases are now "22", so the
  // previous two lines collapsed to the same key and the 100K line silently
  // overwrote the 45K one -- routing every "22" lookup (e.g. the desktop app's
  // product ID over WebSocket) to the 100K product, even for 45K customers.
  // Tier routing happens separately through the Features map, not this one.
  [QLM_PRODUCT_ID_TSL_45K]: TSL_45K_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL]: LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  [QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL]: LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  // Unified (SK-U) products — mirror backend QLM_TO_STRIPE_PRODUCT_MAPPING.
  [QLM_PRODUCT_ID_MSR_V6]: MSR_V6_PRODUCT_ID,
  [QLM_PRODUCT_ID_DICTATION_PRO_V2]: DICTATION_PRO_V2_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_PRO]: MSR_PRO_PRODUCT_ID,
  [QLM_PRODUCT_ID_AUTO_FTP_V8]: AUTO_FTP_V8_PRODUCT_ID,
  [QLM_PRODUCT_ID_SECURITY_MONITOR_PRO]: SECURITY_MONITOR_PRO_PRODUCT_ID,
  [QLM_PRODUCT_ID_WEBCAM_MONITOR]: WEBCAM_MONITOR_PRODUCT_ID,
  [QLM_PRODUCT_ID_TSL_TRIAL]: TSL_TRIAL_PRODUCT_ID,
  tsl: TSL_TRIAL_PRODUCT_ID,
  dt: DOCUMENT_TRANSLATOR_PRODUCT_ID,
};

export const TSL_TRIAL_PRODUCT = TSL_TRIAL_PRODUCT_ID;

/**
 * Set of all paid TSL Stripe product IDs (excluding the trial product).
 * Used by the subscription adapter's plan-upgrade fallback and the
 * empty-card suppression filter (SK-1, SK-2 from S306 feedback).
 */
export const TSL_PAID_PRODUCTS = new Set<string>([
  TSL_45K_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
]);

/**
 * Stripe Product ID to QLM Product ID mapping.
 * Used for matching legacy licenses (those without StripeProductId)
 * to products by their QLM ProductID.
 *
 * Source: Backend QLM_TO_STRIPE_PRODUCT_MAPPING (apps/core/constants.py)
 */
export const STRIPE_TO_QLM_PRODUCT_MAP: Record<string, number> = {
  [LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID]:        Number(QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL),
  [LEGACY_MSR_ANNUAL_PRODUCT_ID]:             Number(QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL),
  [TSL_45K_PRODUCT_ID]:                       Number(QLM_PRODUCT_ID_TSL_45K),
  [TSL_100K_PRODUCT_ID]:                      Number(QLM_PRODUCT_ID_TSL_100K),
  [LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID]:   Number(QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL),
  [LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID]:         Number(QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL),
  [TSL_TRIAL_PRODUCT_ID]:                     Number(QLM_PRODUCT_ID_TSL_TRIAL),
  // NF-2: Unified product IDs — DD-3 (FCR Pass 6): confirmed QLM ids from backend constants.py.
  // These were 0 (placeholder) which disabled ProductID matching for all six products.
  [MSR_V6_PRODUCT_ID]:               Number(QLM_PRODUCT_ID_MSR_V6),
  [DICTATION_PRO_V2_PRODUCT_ID]:     Number(QLM_PRODUCT_ID_DICTATION_PRO_V2),
  [MSR_PRO_PRODUCT_ID]:              Number(QLM_PRODUCT_ID_MSR_PRO),
  [AUTO_FTP_V8_PRODUCT_ID]:          Number(QLM_PRODUCT_ID_AUTO_FTP_V8),
  [SECURITY_MONITOR_PRO_PRODUCT_ID]: Number(QLM_PRODUCT_ID_SECURITY_MONITOR_PRO),
  [WEBCAM_MONITOR_PRODUCT_ID]:       Number(QLM_PRODUCT_ID_WEBCAM_MONITOR),
};

/**
 * Maps the stable QLM Features field value to the Stripe product ID for TSL.
 *
 * Secondary defensive layer used when StripeProductId cannot be resolved from
 * UserData1 (e.g. after an add-on purchase sets UserData1 to a cumulative total
 * like "55000" that has no static mapping). The Features field is set once at
 * license creation and is never changed by add-on purchases, making it reliable.
 *
 * Trailing-semicolon variants included for QLM server version compatibility.
 * Mirror of backend TSL_FEATURES_TO_STRIPE_PRODUCT_MAPPING in apps/core/constants.py.
 */
/**
 * The one definition of the tier literal on this side. Mirrors
 * TSL_TIER_FEATURES in backend apps/core/constants.py.
 *
 * The value is a QLM feature-set value, so it is a bit flag: a set holds eight
 * features with IDs 1, 2, 4, 8, 16, 32, 64 and 128, OR'd together, so 255 is
 * the most it can hold and a word quota cannot live there.
 * Tier bits: 1 = Trial, 2 = 45K, 4 = 100K.
 */
export const TSL_TIER_FEATURES: Record<string, string> = {
  [TSL_TRIAL_PRODUCT_ID]: "0:1;",
  [TSL_45K_PRODUCT_ID]:   "0:2;",
  [TSL_100K_PRODUCT_ID]:  "0:4;",
};

/** Quota literals from before the standard. Read only; nothing writes these. */
const TSL_LEGACY_QUOTA_FEATURES: Record<string, string> = {
  "0:45000":  TSL_45K_PRODUCT_ID,
  "0:100000": TSL_100K_PRODUCT_ID,
};

/**
 * EX-14 (Pass 10): built the same way as the backend map, from the same two
 * sources, so the two cannot hold different key sets. It previously described
 * itself as a mirror while missing the trial entries entirely.
 *
 * Every literal the application has ever written is accepted, with and without
 * the trailing semicolon, because QLM servers differ on returning it.
 */
const buildTslFeaturesToProductMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const [literal, productId] of Object.entries(
    TSL_LEGACY_QUOTA_FEATURES,
  )) {
    const base = literal.replace(/;+$/, "");
    map[base] = productId;
    map[`${base};`] = productId;
  }
  for (const [productId, literal] of Object.entries(TSL_TIER_FEATURES)) {
    if (!productId || !literal) continue;
    const base = literal.replace(/;+$/, "");
    if (map[base] === undefined) map[base] = productId;
    if (map[`${base};`] === undefined) map[`${base};`] = productId;
  }
  return map;
};

export const TSL_FEATURES_TO_PRODUCT_MAP: Record<string, string> =
  buildTslFeaturesToProductMap();

/**
 * AJ-2 (Pass 20): how each product reaches the customer, not only where it lives.
 *
 * Products differ in HOW they are distributed. A direct installer is a file the
 * browser saves; a Store listing is a page the customer opens; a download page is
 * a page that offers the file. One map of bare addresses could not express that,
 * and treating a Store page as an installer is what made the Download Installer
 * button open a web page under a label promising a file.
 *
 * `pageUrl` is always the product's page on the Deskshare website, built by
 * getProductPageUrl() below, and stays synced with the backend equivalent
 * (apps/core/constants.py), which is what emails link to. `url` is where the
 * button sends the customer.
 *
 * Installer addresses are only recorded here once verified against Deskshare's own
 * published download page. Anything not yet verified stays `page`, which opens the
 * correct product page under an honest label rather than a guessed file address
 * that would 404 under a button promising a download.
 */
export type ProductDistribution = {
  kind: "installer" | "store" | "page";
  /** Where the button sends the customer. */
  url: string;
  /** The product's page on the Deskshare website. Always browsable. */
  readonly pageUrl: string;
};

/**
 * B-5 (S620): the website's own product slugs, verified against the live site.
 *
 * The addresses these replace were of the form `/downloads/{slug}`. No such path
 * exists on www.deskshare.com: every one of them redirects to the home page, so
 * a customer following "Open Download Page" from the thank-you page, the
 * dashboard, the activation dialog or a purchase e-mail landed on the home page
 * instead of their product -- several times over, from several surfaces.
 *
 * English takes no prefix; the five other website languages take a two-letter
 * one (Brazilian Portuguese is /pt/, not /pt-BR/). That prefixing rule is
 * already implemented once, in desksharePath() -- it is reused here rather than
 * written a second time.
 */
const WEBSITE = DESKSHARE_WEBSITE_URL;

export const PRODUCT_PAGE_SLUG: Record<string, string> = {
  [DICTATION_PRO_V2_PRODUCT_ID]:              "dictation",
  [LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID]:   "dictation",
  [DOCUMENT_TRANSLATOR_PRODUCT_ID]:           "document-translator",
  [AUTO_FTP_V8_PRODUCT_ID]:                   "ftp-client",
  [LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID]:        "ftp-client",
  [WEBCAM_MONITOR_PRODUCT_ID]:                "wcm",
  [MSR_V6_PRODUCT_ID]:                        "screen-recorder",
  [LEGACY_MSR_ANNUAL_PRODUCT_ID]:             "screen-recorder",
  [TSL_45K_PRODUCT_ID]:                       "text-to-speech-software",
  [TSL_100K_PRODUCT_ID]:                      "text-to-speech-software",
  [TSL_TRIAL_PRODUCT_ID]:                     "text-to-speech-software",
  [SECURITY_MONITOR_PRO_PRODUCT_ID]:          "video-surveillance-software",
  // Verified live: /video-screen-capture redirects to the My Screen Recorder
  // Pro page, and /de/video-screen-capture to its German version. Neither
  // /my-screen-recorder-pro nor /screen-recorder-pro exists.
  [MSR_PRO_PRODUCT_ID]:                       "video-screen-capture",
  [LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID]:         "video-screen-capture",
};

/** The listing page, for a product with no entry. Singular, and it answers 200. */
export const DEFAULT_DOWNLOAD_URL = `${WEBSITE}/download`;

/**
 * The product's page on the Deskshare website, in the customer's language.
 *
 * `lang` defaults to the language of the shop's own address, which is what
 * desksharePath() reads when it is not told otherwise.
 */
export function getProductPageUrl(productId: string, lang?: string): string {
  const slug = PRODUCT_PAGE_SLUG[productId];
  if (!slug) return DEFAULT_DOWNLOAD_URL;
  return `${WEBSITE}${desksharePath(`/${slug}`, lang)}`;
}

// B-5: installer addresses are unchanged. Every one was verified live during the
// S620 review; only the PAGE addresses were wrong.
// S660: the file PATHS are unchanged; only the host is now the configured one,
// for the same reason the product pages above already are -- three more copies
// of the production address is three more links a feature shop sends to the live
// site.
const AUTO_FTP_INSTALLER = `${WEBSITE}/download/afm/AutoFTPManager.exe`;
const MSR_PRO_INSTALLER = `${WEBSITE}/download/msrp/MyScreenRecorderPro.exe`;
const SMP_INSTALLER = `${WEBSITE}/download/smp/SecurityMonitorPro.exe`;

// B-5: `pageUrl` is a getter, not a value frozen when this module is evaluated.
// getProductPageUrl() reads the language from the shop's current address, and
// the map is built once at import; a plain value would pin every product page to
// whichever language the application happened to boot in. A getter keeps this
// map and getProductDownloadUrl() answering with the same single function.
const installer = (url: string, productId: string): ProductDistribution => ({
  kind: "installer",
  url,
  get pageUrl() {
    return getProductPageUrl(productId);
  },
});
const page = (productId: string): ProductDistribution => ({
  kind: "page",
  get url() {
    return getProductPageUrl(productId);
  },
  get pageUrl() {
    return getProductPageUrl(productId);
  },
});

export const PRODUCT_DISTRIBUTION: Record<string, ProductDistribution> = {
  [LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID]:        installer(AUTO_FTP_INSTALLER, LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID),
  [AUTO_FTP_V8_PRODUCT_ID]:                   installer(AUTO_FTP_INSTALLER, AUTO_FTP_V8_PRODUCT_ID),
  [LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID]:         installer(MSR_PRO_INSTALLER, LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID),
  [MSR_PRO_PRODUCT_ID]:                       installer(MSR_PRO_INSTALLER, MSR_PRO_PRODUCT_ID),
  [SECURITY_MONITOR_PRO_PRODUCT_ID]:          installer(SMP_INSTALLER, SECURITY_MONITOR_PRO_PRODUCT_ID),
  // S682 Item 2: website-first distribution. The Microsoft Store listing remains
  // a secondary channel and is not the destination of the shop's download button.
  //
  // This entry drives THREE surfaces -- the My Products download button, the
  // activation dialog and the licence summary page -- so all three sent Document
  // Translator customers to the Store, while the product adapter already
  // published www.deskshare.com/document-translator for the same product. The
  // shop held two different answers for one product; this removes the
  // disagreement rather than adding a third.
  //
  // page() is not merely a different address: its `url` is a getter over
  // getProductPageUrl(), so it answers in the customer's own language -- a German
  // customer is sent to /de/document-translator. The hard-coded Store address
  // could never do that.
  //
  // The comment this replaces said "there is no standalone installer file", which
  // is not an argument for sending customers to the Microsoft Store. Every other
  // website-first product in this map has no verified installer either and every
  // one is registered as a page. Dictation Pro is the clearest comparison: it is
  // published on the Microsoft Store as well, and it is registered as a page.
  //
  // No installer address is added, because none has been verified -- this file's
  // standing rule is that an installer address is recorded only once verified
  // against the published download page.
  [DOCUMENT_TRANSLATOR_PRODUCT_ID]: page(DOCUMENT_TRANSLATOR_PRODUCT_ID),
  // Installer file addresses not yet confirmed — see the Website team item in HANDOFF.md.
  [LEGACY_MSR_ANNUAL_PRODUCT_ID]:             page(LEGACY_MSR_ANNUAL_PRODUCT_ID),
  [MSR_V6_PRODUCT_ID]:                        page(MSR_V6_PRODUCT_ID),
  [LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID]:   page(LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID),
  [DICTATION_PRO_V2_PRODUCT_ID]:              page(DICTATION_PRO_V2_PRODUCT_ID),
  [TSL_45K_PRODUCT_ID]:                       page(TSL_45K_PRODUCT_ID),
  [TSL_100K_PRODUCT_ID]:                      page(TSL_100K_PRODUCT_ID),
  [TSL_TRIAL_PRODUCT_ID]:                     page(TSL_TRIAL_PRODUCT_ID),
  [WEBCAM_MONITOR_PRODUCT_ID]:                page(WEBCAM_MONITOR_PRODUCT_ID),
};

/**
 * How a product is distributed, or undefined when the product is unknown.
 * AJ-2: this is the one place that answers the question.
 */
export function getProductDistribution(productId: string): ProductDistribution | undefined {
  return PRODUCT_DISTRIBUTION[productId];
}

/**
 * The product's page on the Deskshare website. Read by adapters and anything
 * that needs a browsable address rather than the button's destination.
 * B-5: one implementation -- getProductPageUrl -- which also supplies the
 * `pageUrl` of every PRODUCT_DISTRIBUTION entry, so the two cannot drift.
 */
export function getProductDownloadUrl(productId: string, lang?: string): string {
  return getProductPageUrl(productId, lang);
}

// GP-8 (Pass 20): products bought by the unit rather than licensed by tier.
// A consumable has no Annual/Monthly identity, so it is exempt from licence-tier
// name suffixes. This used to be inferred from a list of deprecated products,
// which held Extra Words purely to keep the suffix off it -- two ideas sharing
// one list and pulling in opposite directions. Extra Words is a live product
// again, so the exemption is stated here on its own terms.
export const CONSUMABLE_PRODUCT_IDS = new Set<string>([
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  TSL_EXTRA_WORDS_PRODUCT_ID,
]);

// VF-1: Trial length is PER PRODUCT, not global. Text Speaker Live and Dictation
// Pro run seven days, WebCam Monitor fourteen; every other product runs three. A single
// shared number here would put the wrong length in front of one of them.
//
// This map mirrors TRIAL_DURATION_DAYS_BY_APP in backend/config/settings.py and
// TrialManager::trialDurationSeconds() in DSControls. All three carry the same
// numbers and are changed together, exactly as APP_NAME_DISPLAY is kept in step.
// Both canonical ("TextSpeakerLive") and spaced ("Text Speaker Live") keys are
// accepted, because these pages render display names.
// Decision 12.3 (S607): Dictation Pro joins Text Speaker Live on 7 days. The
// published Microsoft Store listing states a seven-day trial, and the earlier
// three-day decision left the shop enforcing a shorter trial than the company
// advertises for the same product.
export const TRIAL_DURATION_DAYS_DEFAULT = 3;
export const TRIAL_DURATION_DAYS_BY_APP: Record<string, number> = {
  TextSpeakerLive: 7,
  "Text Speaker Live": 7,
  DictationPro: 7,
  "Dictation Pro": 7,
  // FCR S760: WebCam Monitor runs fourteen days, as DS Controls gives the
  // desktop (TrialManager::getTrialDurationSecondsFor) and the server records.
  WebCamMonitor: 14,
  "WebCam Monitor": 14,
};

/**
 * Trial length in whole days for one product. Unknown products get the default.
 *
 * S609: matched the way the server matches it -- spaces collapsed, case ignored.
 *
 * The server's accessor already does this, and says why: a caller passing the
 * spaced display name where the map holds the canonical one would otherwise be
 * handed the default while the desktop enforced seven days. This did an exact
 * lookup against four hard-coded spellings, so the two ends disagreed for any
 * fifth. That is not hypothetical -- handoff item W-81 exists precisely because
 * the exact string the desktop sends for Dictation Pro is not confirmed, and the
 * reassurance given there ("the server matches either spelling by collapsing
 * spaces, so an ordinary value works either way") was true of the server and
 * false of the browser. **The browser is where the customer reads the number**:
 * a mismatch renders "Your 3-day free trial" directly above an end date seven
 * days out.
 */
export function trialDurationDays(appName?: string): number {
  return perAppValue(TRIAL_DURATION_DAYS_BY_APP, appName, TRIAL_DURATION_DAYS_DEFAULT);
}

/**
 * How long one product's trial verification link lasts, in minutes. Mirrors the
 * server's TRIAL_TOKEN_EXPIRY_MINUTES / TRIAL_TOKEN_EXPIRY_MINUTES_BY_APP
 * (backend/config/settings.py), matched by the same rule as the trial length:
 * Dictation Pro's link lasts 24 hours, every other product's 15 minutes. The
 * trial pages state this number, so it has to be the product's own.
 */
export const TRIAL_LINK_VALID_MINUTES_DEFAULT = 15;
export const TRIAL_LINK_VALID_MINUTES_BY_APP: Record<string, number> = {
  DictationPro: 24 * 60,
};

export function trialLinkValidMinutes(appName?: string): number {
  return perAppValue(TRIAL_LINK_VALID_MINUTES_BY_APP, appName, TRIAL_LINK_VALID_MINUTES_DEFAULT);
}

/**
 * "15 minutes", "24 hours", "24 Stunden" -- how long a link stays valid, in the reader's
 * language. Whole hours are stated in hours. Intl's unit names are the same
 * words the six locale files used before the number became per product.
 */
export function formatLinkValidity(minutes: number, locale: string): string {
  const inHours = minutes >= 60 && minutes % 60 === 0;
  const value = inHours ? minutes / 60 : minutes;
  try {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: inHours ? "hour" : "minute",
      unitDisplay: "long",
    }).format(value);
  } catch {
    return `${value} ${inHours ? (value === 1 ? "hour" : "hours") : value === 1 ? "minute" : "minutes"}`;
  }
}

/** One product's value from a per-product map, matched the way the server matches. */
function perAppValue(byApp: Record<string, number>, appName: string | undefined, fallback: number): number {
  const key = (appName || "").trim();
  // Object.hasOwn, not a bare lookup: `TRIAL_DURATION_DAYS_BY_APP["toString"]`
  // returns a function, and `["__proto__"]` an object -- both !== undefined, and
  // both would be handed back as if they were a number of days. Python's `in`
  // has no prototype to inherit from, so a bare lookup here is the one input
  // class on which the two ends would still disagree.
  const exact = Object.hasOwn(byApp, key)
    ? byApp[key]
    : undefined;
  if (exact !== undefined) return exact;

  // Literal spaces only, to match the server's rule exactly. Collapsing all
  // whitespace would be defensible on its own, but it would make the browser
  // match a name (one containing a tab or a non-breaking space) that the server
  // would not -- so the customer would be told seven days while the server
  // enforced three. Two rules that differ at the edges are what this is here to
  // stop; if the rule should widen, widen both together.
  const collapsed = key.replace(/ /g, "").toLowerCase();
  if (!collapsed) return fallback;

  for (const [name, value] of Object.entries(byApp)) {
    if (name.replace(/ /g, "").toLowerCase() === collapsed) return value;
  }
  return fallback;
}


